import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { TabNav, TabNavItem } from '../../../components/ui/tabNav/tabNav.component';
import { useHasPermission } from '../../../contexts/auth';
import { TENANT_ROUTES } from '../tenantConfig.routes';
import styles from './viewTenant.module.css';

/**
 * Tenant Config, arranged as four sections rather than one long tab strip:
 * who the workspace is, who may use it, how it is configured, and what it does
 * on its own. Each section owns its sub-tabs; this bar only picks between them.
 */
export const TenantConfig = () => {
  const { t } = useTranslation();
  const canViewUsers = useHasPermission('users.view_user');
  const canViewGroups = useHasPermission('auth.view_group');
  const canViewSettings = useHasPermission('config.view_tenantsettings');
  const canViewSchemas = useHasPermission('settlement.view_numerationschema');
  const canViewTransitionActions = useHasPermission('service.view_jobstatustransitionaction');
  const canViewSettlementAutomation = useHasPermission('settlement.view_contract');

  const tabs: TabNavItem[] = [
    {
      key: 'workspace',
      label: t('Workspace'),
      // Every other section sits on a longer prefix, so "/tenant" stays
      // selected only for the pages that really belong to this one.
      path: TENANT_ROUTES.overview,
    },
    ...(canViewUsers || canViewGroups
      ? [
          {
            key: 'access',
            label: t('Users & permissions'),
            path: canViewUsers ? TENANT_ROUTES.users : TENANT_ROUTES.permissionGroups,
            alsoMatches: [TENANT_ROUTES.users, TENANT_ROUTES.permissionGroups],
          },
        ]
      : []),
    ...(canViewSettings || canViewSchemas
      ? [
          {
            key: 'settings',
            label: t('Settings'),
            // Numbering is the one settings page a schemas-only viewer can read.
            path: canViewSettings ? TENANT_ROUTES.settings : TENANT_ROUTES.numbering,
            alsoMatches: [TENANT_ROUTES.settings],
          },
        ]
      : []),
    ...(canViewSettlementAutomation || canViewTransitionActions
      ? [
          {
            key: 'automation',
            label: t('Automation'),
            path: canViewSettlementAutomation ? TENANT_ROUTES.automation : TENANT_ROUTES.jobStatusActions,
            alsoMatches: [TENANT_ROUTES.automation],
          },
        ]
      : []),
  ];

  return (
    <Box className={styles.container}>
      <TabNav tabs={tabs} />
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
